import { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "components/Loadable";
import RequireUnverified from "routes/RequireUnverified";

// Code-split the layout itself (sidebar/menu-items tree), not just its
// pages — this module was eagerly imported before, so its full dependency
// chain (every menu-items/*.jsx) loaded on every route including /login.
const DashboardLayout = Loadable(lazy(() => import("layout/Dashboard")));

const VerificationPage = Loadable(lazy(() => import("pages/verification")));

// Lazy load pages
const Home = Loadable(lazy(() => import("pages/home")));
const Veille = Loadable(lazy(() => import("pages/veille/veille")));
const Alertes = Loadable(lazy(() => import("pages/veille/alertes")));
const VeilleDetails = Loadable(
  lazy(() => import("pages/veille/VeilleDetails")),
);
const Avis = Loadable(lazy(() => import("pages/avis/avis")));
const AvisDetails = Loadable(lazy(() => import("pages/avis/avisDetails")));
const Modules = Loadable(lazy(() => import("pages/modules")));
const Formation = Loadable(lazy(() => import("pages/formation")));
const FormationProfile = Loadable(lazy(() => import("pages/formationProfile")));
const FormationModules = Loadable(lazy(() => import("pages/formationModules")));
const FormationModulePlay = Loadable(
  lazy(() => import("pages/formationModulePlay")),
);
const FormationModulePlayLegacyRedirect = Loadable(
  lazy(() =>
    import("pages/formationModulePlay").then((m) => ({
      default: m.PlayLegacyRedirect,
    })),
  ),
);
const MedicalFormationComingSoon = Loadable(
  lazy(() => import("pages/maintenance/coming-soon/coming-soon2")),
);
const Masterclass = Loadable(lazy(() => import("pages/masterclass")));
const MasterclassProfile = Loadable(lazy(() => import("pages/masterclassProfile")));
const MasterclassModules = Loadable(lazy(() => import("pages/masterclassModules")));
const Agenda = Loadable(lazy(() => import("pages/agenda")));
const EventDetails = Loadable(lazy(() => import("pages/events/EventDetails")));
const Medicaments = Loadable(lazy(() => import("pages/medicament")));
const MedicamentsProfile = Loadable(
  lazy(() => import("pages/medicamentprofile")),
);
const Pathologies = Loadable(lazy(() => import("pages/pathologies")));
const PathologyProfile = Loadable(lazy(() => import("pages/pathologyProfile")));
const Experts = Loadable(lazy(() => import("pages/experts")));
const ExpertProfile = Loadable(lazy(() => import("pages/expertProfile")));
const Institutions = Loadable(lazy(() => import("pages/institutions")));
const InstitutionsProfile = Loadable(
  lazy(() => import("pages/institutionsProfile")),
);
const Learning = Loadable(lazy(() => import("pages/apprentissage")));
const Favorites = Loadable(lazy(() => import("pages/favorites")));
const Newsletter = Loadable(lazy(() => import("pages/newsletter")));
const LibraryPage = Loadable(lazy(() => import("pages/library/library")));
const LibraryDetails = Loadable(lazy(() => import("pages/library/LibraryDetails")));
const ContribuerPage = Loadable(lazy(() => import("pages/contribuer")));
const Certificates = Loadable(lazy(() => import("pages/certificates")));
// const UserProfile = Loadable(lazy(() => import("layout/Profile/user")));

const MeducateAIAssistant = Loadable(lazy(() => import("pages/assistant-ia")));

const AccountProfile = Loadable(lazy(() => import("layout/Profile/account")));
const AccountTabProfile = Loadable(
  lazy(() => import("pages/profile/account/TabProfile")),
);
const AccountTabPersonal = Loadable(
  lazy(() => import("pages/profile/account/TabPersonal")),
);
const AccountTabDashboard = Loadable(
  lazy(() => import("pages/profile/account/dashboard")),
);
const LeaderboardPage = Loadable(
  lazy(() => import("pages/profile/account/dashboard/leaderboard")),
);
const LogsPage = Loadable(
  lazy(() => import("pages/profile/account/dashboard/logs")),
);
const AccountTabPassword = Loadable(
  lazy(() => import("pages/profile/setting/TabPassword")),
);
const AccountTabSettings = Loadable(
  lazy(() => import("pages/profile/setting/TabSettings")),
);
const AccountTabRole = Loadable(
  lazy(() => import("pages/profile/setting/TabRole")),
);
const AccomplishmentVerify = Loadable(
  lazy(() => import("pages/AccomplishmentVerify")),
);

// const TabPersonal = Loadable(
//   lazy(() => import("pages/profile/account/TabPersonal")),
// );
// const UserTabPassword = Loadable(
//   lazy(() => import("pages/profile/account/TabPassword")),
// );
// const UserTabNotifications = Loadable(
//   lazy(() => import("pages/profile/account/TabNotifications")),
// );
const MaintenanceError = Loadable(
  lazy(() => import("pages/maintenance/error/404")),
);

const MainRoutes = {
  path: "/",
  children: [
    {
      path: "/",
      element: <DashboardLayout />,
      children: [
        {
          path: "home",
          element: <Home />,
        },
        {
          path: "formation",
          element: <Formation />,
        },
        {
          path: "formation/:id",
          element: <FormationProfile />,
        },
        {
          path: "formation/:formationId/modules",
          element: <FormationModules />,
        },
        {
          path: "formation/:formationId/modules/:moduleOrder",
          element: <FormationModules />,
        },
        {
          path: "formation/:formationId/apprentissage",
          element: <FormationModulePlay />,
        },
        {
          path: "formation/:formationId/modules/:moduleId/play",
          element: <FormationModulePlayLegacyRedirect />,
        },
        {
          path: "formation/:formationId/modules/:moduleId/play/:screenOrder",
          element: <FormationModulePlayLegacyRedirect />,
        },
        {
          path: "accomplishments/verify/:accomplishmentId",
          element: <AccomplishmentVerify />,
        },
        {
          path: "modules",
          element: <Modules />,
        },
        {
          path: "alertes",
          element: <Alertes />,
        },
        {
          path: "veille",
          element: <Veille />,
        },
        {
          path: "veille/:id",
          element: <VeilleDetails />,
        },
        {
          path: "avis",
          element: <Avis />,
        },
        {
          path: "avis/:id",
          element: <AvisDetails />,
        },
        {
          path: "experts",
          element: <Experts />,
        },
        {
          path: "experts/:id",
          element: <ExpertProfile />,
        },
        {
          path: "institutions",
          element: <Institutions />,
        },
        {
          path: "institutions/:id",
          element: <InstitutionsProfile />,
        },
        {
          path: "masterclass",
          element: <Masterclass />,
        },
        {
          path: "masterclass/:id",
          element: <MasterclassProfile />,
        },
        {
          path: "masterclass/:formationId/modules",
          element: <MasterclassModules />,
        },
        {
          path: "masterclass/:formationId/modules/:moduleOrder",
          element: <MasterclassModules />,
        },
        {
          path: "masterclass/:formationId/apprentissage",
          element: <FormationModulePlay isMasterclass />,
        },
        {
          path: "masterclass/:formationId/modules/:moduleId/play",
          element: <FormationModulePlayLegacyRedirect />,
        },
        {
          path: "masterclass/:formationId/modules/:moduleId/play/:screenOrder",
          element: <FormationModulePlayLegacyRedirect />,
        },
        {
          path: "agenda",
          element: <Agenda />,
        },
        {
          path: "verification",
          element: (
            <RequireUnverified>
              <VerificationPage />
            </RequireUnverified>
          ),
        },
        {
          path: "event/:id",
          element: <EventDetails />,
        },
        {
          path: "medicaments",
          element: <Medicaments />,
        },
        {
          path: "medicaments/:id",
          element: <MedicamentsProfile />,
        },
        {
          path: "pathologies",
          element: <Pathologies />,
        },
        {
          path: "pathologies/:id",
          element: <PathologyProfile />,
        },
        {
          path: "apprentissage",
          element: <Learning />,
        },
        {
          path: "favorites",
          element: <Favorites />,
        },
        {
          path: "newsletter",
          element: <Newsletter />,
        },
        {
          path: "library",
          element: <LibraryPage />,
        },
        {
          path: "library/:id",
          element: <LibraryDetails />,
        },
        {
          path: "contribuer",
          element: <ContribuerPage />,
        },
        {
          path: "certificats",
          element: <Certificates />,
        },
        // Add the new assistant-ia route
        {
          path: "assistant-ia",
          element: <MeducateAIAssistant />,
        },
        // Add the new assistant-ia route
        {
          path: "MedicalFormationComingSoon",
          element: <MedicalFormationComingSoon />,
        },
        {
          path: "profile",
          children: [
            {
              path: "account",
              // element: <AccountProfile />,
              children: [
                {
                  path: "basic",
                  element: <AccountTabProfile />,
                },
                {
                  path: "personal",
                  element: <AccountTabPersonal />,
                },
                {
                  path: "dashboard",
                  children: [
                    {
                      index: true,
                      element: <AccountTabDashboard />,
                    },
                    {
                      path: "leaderboard",
                      element: <LeaderboardPage />,
                    },
                    {
                      path: "logs",
                      element: <LogsPage />,
                    },
                  ],
                },
              ],
            },
            {
              path: "setting",
                element: <AccountProfile />,
                children: [
                  {
                    path: "interests",
                    element: <AccountTabRole />,
                  },
                  {
                    path: "my-account",
                    element: <AccountTabPassword />,
                  },
                  {
                    path: "notification",
                    element: <AccountTabSettings />,
                  },
                  {
                    path: "settings",
                    element: <Navigate to="../notification" replace />,
                  },
                  {
                    path: "verification",
                    element: <Navigate to="/verification" replace />,
                  },
                ],
            },
          ],
          // children: [
          //   {
          //     path: "setting",
          //     element: <AccountProfile />,
          //     children: [
          //       {
          //         path: "interests",
          //         element: <AccountTabRole />,
          //       },
          //       {
          //         path: "my-account",
          //         element: <AccountTabPassword />,
          //       },
          //       {
          //         path: "settings",
          //         element: <AccountTabSettings />,
          //       },
          //     ],
          //   },
          // ],
        },
        {
          path: "formationProfile",
          element: <Navigate to="/formation" replace />,
        },
      ],
    },
    {
      path: "*",
      element: <MaintenanceError />,
    },
  ],
};

export default MainRoutes;
